import { BookableStorageLocation, Booking, GoogleCalendarEvent, IHoliday } from '@luggagehero/shared/interfaces';
import { SharedUtilDate, TimeFormatDetectionVersion } from '@luggagehero/shared/util';

/** @deprecated Use `SharedUtilDate` instead */
export class DateUtil {
  public static get TimeFormatDetectionVersion(): TimeFormatDetectionVersion {
    return SharedUtilDate.TimeFormatDetectionVersion;
  }
  public static set TimeFormatDetectionVersion(value: TimeFormatDetectionVersion) {
    SharedUtilDate.TimeFormatDetectionVersion = value;
  }

  static get is12HourMode(): boolean {
    return SharedUtilDate.is12HourMode;
  }

  static get is24HourMode(): boolean {
    return SharedUtilDate.is24HourMode;
  }

  static getStartOfToday(): Date {
    return SharedUtilDate.getStartOfToday();
  }

  static setTime(date: Date, h = 0, m = 0, s = 0): Date {
    return SharedUtilDate.setTime(date, h, m, s);
  }

  static getEndOfOneYearFromToday(): Date {
    return SharedUtilDate.getEndOfOneYearFromToday();
  }

  static getValidTo(from: Date, to: Date, isMobile = false): Date {
    return SharedUtilDate.getValidTo(from, to, isMobile);
  }

  static getValidFrom(from: Date, to: Date): Date {
    return SharedUtilDate.getValidFrom(from, to);
  }

  static isAtLeastOneDayLater(from: Date, to: Date): boolean {
    return SharedUtilDate.isAtLeastOneDayLater(from, to);
  }

  static isToday(date: Date): boolean {
    return SharedUtilDate.isToday(date);
  }

  static isSameDate(date1: Date, date2: Date): boolean {
    return SharedUtilDate.isSameDate(date1, date2);
  }

  static isSameDateAsHoliday(date1: Date, holiday: GoogleCalendarEvent | IHoliday): boolean {
    return SharedUtilDate.isSameDateAsHoliday(date1, holiday);
  }

  static matchHoliday(date: Date, holidays: GoogleCalendarEvent[] | IHoliday[]): GoogleCalendarEvent | IHoliday | null {
    return SharedUtilDate.matchHoliday(date, holidays);
  }

  static isValidHoliday(holiday: GoogleCalendarEvent | IHoliday): boolean {
    return SharedUtilDate.isValidHoliday(holiday);
  }

  static isPastEvent(event: GoogleCalendarEvent): boolean {
    return SharedUtilDate.isPastEvent(event);
  }

  static formatEventDate(event: GoogleCalendarEvent): string {
    return SharedUtilDate.formatEventDate(event);
  }

  static formatTime(hour: string, minute: string, shorten: boolean): string {
    return SharedUtilDate.formatTime(hour, minute, shorten);
  }

  static formatTimeComponent(value: number, isHour: boolean): string;
  static formatTimeComponent(value: string, isHour: boolean): string;
  static formatTimeComponent(timeAsNumOrStr: number | string, isHour: boolean): string {
    return SharedUtilDate.formatTimeComponent(timeAsNumOrStr as string, isHour);
  }

  static isSameTime(date1: Date, date2: Date): boolean {
    return SharedUtilDate.isSameTime(date1, date2);
  }

  static getTimeIndexOf(time: Date, times: Date[]): number {
    return SharedUtilDate.getTimeIndexOf(time, times);
  }

  static getDateIndexOf(date: Date, dates: Date[]): number {
    return SharedUtilDate.getDateIndexOf(date, dates);
  }

  static roundTimeHalfHour(time: Date): Date {
    return SharedUtilDate.roundTimeHalfHour(time);
  }

  static hoursBetween(fromDateTime: Date | string, toDateTime: Date | string, gracePeriod = 0): number {
    return SharedUtilDate.hoursBetween(fromDateTime, toDateTime, gracePeriod);
  }

  static daysBetween(fromDateTime: Date | string, toDateTime: Date | string): number {
    return SharedUtilDate.daysBetween(fromDateTime, toDateTime);
  }

  static minutesBetween(fromDateTime: Date, toDateTime: Date) {
    return SharedUtilDate.minutesBetween(fromDateTime, toDateTime);
  }

  static minutesSinceCheckIn(booking: Booking): number {
    return SharedUtilDate.minutesSinceCheckIn(booking);
  }

  static isBookingForToday(booking: Booking): boolean {
    return SharedUtilDate.isBookingForToday(booking);
  }

  static addDays(date: Date, days: number, endOfMonth = false): Date {
    return SharedUtilDate.addDays(date, days, endOfMonth);
  }

  static addHours(date: Date, hours: number): Date {
    return SharedUtilDate.addHours(date, hours);
  }

  static addMinutes(date: Date, minutes: number): Date {
    return SharedUtilDate.addMinutes(date, minutes);
  }

  static subtractMinutes(date: Date, minutes: number): Date {
    return SharedUtilDate.subtractMinutes(date, minutes);
  }

  static serializeDate(date: Date, includeTime = true): string {
    return SharedUtilDate.serializeDate(date, includeTime);
  }

  static serializeDateUtc(date: Date, includeTime = true): string {
    return SharedUtilDate.serializeDateUtc(date, includeTime);
  }

  static deserializeDate(date: string, allowPast = true, fallback?: Date): Date {
    return SharedUtilDate.deserializeDate(date, allowPast, fallback);
  }

  static deserializeDateTz(date: string, timezone: string, allowPast = true, fallback?: Date): Date {
    return SharedUtilDate.deserializeDateTz(date, timezone, allowPast, fallback);
  }

  static dateToNumber(value: Date): number {
    return SharedUtilDate.dateToNumber(value);
  }

  static dateToTimeOfDay(date: Date): number {
    return SharedUtilDate.dateToTimeOfDay(date);
  }

  static getStorageLocationTimeOfDay(shop: BookableStorageLocation): number {
    return SharedUtilDate.getStorageLocationTimeOfDay(shop);
  }

  static getStorageLocationNow(shop: BookableStorageLocation): Date {
    return SharedUtilDate.getStorageLocationNow(shop);
  }

  static parseDateParam(date: string): Date {
    return SharedUtilDate.parseDateParam(date);
  }

  static isValidPeriod(from: Date, to: Date): boolean {
    return SharedUtilDate.isValidPeriod(from, to);
  }
}
